import type {ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import Sortable from 'sortablejs';
import {InputField} from '@Components/input-field-v2';
import type {InputType} from '@Components/input-field-v2/input-field.types';
import {InputFieldType} from '@Components/input-field-v2/input-field.types';
import {LabelledCheckbox} from '@Components/labelled-checkbox';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Button, ButtonType, ButtonSize} from '@Components/button';
import {TicketingQuestionTypeDropdown} from '@Components/event-wizard/event-wizard-ticketing/components/ticketing-question-type-dropdown/ticketing-question-type-dropdown';
import {TicketingQuestionEditorBox} from '@Components/event-wizard/event-wizard-ticketing/components/ticketing-question-editor-box/ticketing-question-editor-box';
import type {TicketingQuestion} from '@Components/event-wizard/event-wizard-ticketing/event-wizard-ticketing.types';
import {DEFAULT_OPTION_COUNT, QuestionInputType} from '@Components/event-wizard/event-wizard-ticketing/event-wizard-ticketing.types';
import {isTicketingQuestionValid, requiresOptions, revertSortableDomMove} from '@Components/event-wizard/event-wizard-ticketing/event-wizard-ticketing.helper';
import {moveItem} from '@Utils/array.util';
import {getUniqueString} from '@Utils/string.util';
import type {TicketingOptionRow, TicketingQuestionEditorProps} from './ticketing-question-editor.types';
import {TICKETING_OPTION_DRAG_HANDLE} from './ticketing-question-editor.types';
import styles from './ticketing-question-editor.module.scss';
import {CHECKBOX_TYPE} from '@Components/checkbox-legacy';

const toOptionRows = (options: string[]): TicketingOptionRow[] => {
  return options.map((value) => ({id: getUniqueString(), value}));
};

const blankOptionRows = (count: number): TicketingOptionRow[] => {
  return Array.from({length: count}, () => ({id: getUniqueString(), value: ''}));
};

export function TicketingQuestionEditor({draft, mode, displayNumber, onDraftChange, onCommit, onCancel}: TicketingQuestionEditorProps): ReactElement {
  const optionsListRef = useRef<HTMLDivElement | null>(null);
  const optionRowsRef = useRef<TicketingOptionRow[]>([]);
  const draftRef = useRef<TicketingQuestion>(draft);

  const seedOptionRows = (): TicketingOptionRow[] => {
    if (!requiresOptions(draft.type)) {
      return [];
    }
    return draft.options.length > 0 ? toOptionRows(draft.options) : blankOptionRows(DEFAULT_OPTION_COUNT);
  };

  const [optionRows, setOptionRows] = useState<TicketingOptionRow[]>(seedOptionRows);

  // Keep the latest optionRows + draft on refs (synced after every render) so the Sortable `onEnd`
  // closure — frozen when its effect ran on `[isChoiceType]` — reads current values instead of the
  // render-time props it captured. A typed-then-dragged question would otherwise revert text/required.
  useEffect(() => {
    optionRowsRef.current = optionRows;
    draftRef.current = draft;
  });

  const isChoiceType = requiresOptions(draft.type);
  const canCommit = isTicketingQuestionValid(draft);
  const commitLabel = mode === 'edit' ? window.i18next.t('pmwjs_event_ticketing_save_changes') : window.i18next.t('pmwjs_event_ticketing_add_question_cta');

  /**
   * Sets the option rows and projects their values onto the draft. Reads `draftRef.current` (not the
   * `draft` prop) so it stays correct when called from the Sortable `onEnd` closure, which captured a
   * stale `draft` — otherwise dragging an option would revert the question text / required edits.
   */
  const applyOptionRows = (rows: TicketingOptionRow[]): void => {
    setOptionRows(rows);
    onDraftChange({...draftRef.current, options: rows.map((row) => row.value)});
  };

  const onTextChange = (value: InputType): void => {
    onDraftChange({...draft, text: value.toString()});
  };

  const onRequiredChange = (isChecked: boolean): void => {
    onDraftChange({...draft, required: isChecked});
  };

  const onTypeChange = (type: QuestionInputType): void => {
    const becomesChoice = requiresOptions(type);
    const nextRows = becomesChoice ? (optionRows.length > 0 ? optionRows : blankOptionRows(DEFAULT_OPTION_COUNT)) : [];
    setOptionRows(nextRows);

    // Phone questions use a fixed 'Phone number' title and hide the title input; switching a phone
    // question to another type clears that auto-filled title so the new name starts empty.
    let text = draft.text;
    if (type === QuestionInputType.PHONE) {
      text = window.i18next.t('pmwjs_event_ticketing_type_phone');
    } else if (draft.type === QuestionInputType.PHONE) {
      text = '';
    }

    onDraftChange({...draft, type, text, options: nextRows.map((row) => row.value)});
  };

  const onOptionValueChange = (id: string, value: InputType): void => {
    applyOptionRows(optionRows.map((row) => (row.id === id ? {...row, value: value.toString()} : row)));
  };

  const onOptionAdd = (): void => {
    applyOptionRows([...optionRows, {id: getUniqueString(), value: ''}]);
  };

  const onOptionRemove = (id: string): void => {
    applyOptionRows(optionRows.filter((row) => row.id !== id));
  };

  useEffect(() => {
    if (!optionsListRef.current || !isChoiceType) {
      return undefined;
    }

    const sortableInstance = Sortable.create(optionsListRef.current, {
      handle: `.${TICKETING_OPTION_DRAG_HANDLE}`,
      animation: 150,
      chosenClass: styles.optionRowChosen,
      dragClass: styles.optionRowDragging,
      onEnd: (event): void => {
        const moved = revertSortableDomMove(event);
        if (!moved || event.oldIndex === undefined || event.newIndex === undefined) {
          return;
        }
        applyOptionRows(moveItem(optionRowsRef.current, event.oldIndex, event.newIndex));
      },
    });

    return (): void => {
      sortableInstance.destroy();
    };
  }, [isChoiceType]);

  const getOptionsList = (): ReactElement | null => {
    if (!isChoiceType) {
      return null;
    }

    return (
      <div className="flex-column-align-start _full-width">
        <div ref={optionsListRef} className="gap-2 flex-column-align-start _full-width">
          {optionRows.map((row, index) => {
            return (
              <div className={`${styles.optionRow} gap-2 flex-row-align-center _full-width`} key={row.id}>
                <Icon className={`${styles.dragHandle} ${TICKETING_OPTION_DRAG_HANDLE} ct-neutral-secondary`} icon="icon-draggable" size={IconSize.SIZE_ICON_16} />
                <InputField
                  className="flex-1"
                  inputClassName="corner-radius-8"
                  input={row.value}
                  onInputChange={(value: InputType): void => {
                    onOptionValueChange(row.id, value);
                  }}
                  placeholder={window.i18next.t('pmwjs_event_ticketing_option_placeholder', {n: index + 1})}
                  inputType={InputFieldType.TEXT}
                />
                <Icon
                  className={`${styles.optionRemove} ct-neutral-secondary`}
                  icon="icon-delete"
                  size={IconSize.SIZE_ICON_16}
                  onClick={(): void => {
                    onOptionRemove(row.id);
                  }}
                  ariaLabel={window.i18next.t('pmwjs_delete')}
                  isUsedAsButton
                />
              </div>
            );
          })}
        </div>
        <Button
          customClasses={`${styles.addOptionsBtn} spacing-m-l-3 spacing-p-t-2 spacing-m-t-2`}
          type={ButtonType.TERTIARY}
          size={ButtonSize.SMALL}
          icon="icon-plus"
          text={window.i18next.t('pmwjs_event_ticketing_add_option')}
          onClick={onOptionAdd}
        />
      </div>
    );
  };

  const getTitleInput = (): ReactElement | null => {
    // Phone questions use a fixed 'Phone number' title, so the editable title input is hidden.
    if (draft.type === QuestionInputType.PHONE) {
      return null;
    }
    return (
      <InputField
        className="_full-width"
        inputClassName="corner-radius-8"
        input={draft.text}
        onInputChange={onTextChange}
        placeholder={window.i18next.t('pmwjs_event_ticketing_question_placeholder')}
        inputType={InputFieldType.TEXT}
      />
    );
  };

  return (
    <TicketingQuestionEditorBox displayNumber={displayNumber}>
      {getTitleInput()}
      <div className="gap-3 flex-row-align-center align-items-end _full-width">
        <div className="flex-1">
          <TicketingQuestionTypeDropdown value={draft.type} onChange={onTypeChange} />
        </div>
        <LabelledCheckbox
          checkboxType={CHECKBOX_TYPE.PRIMARY_LIGHT}
          className="flex-shrink-0 spacing-p-b-2"
          label={window.i18next.t('pmwjs_event_ticketing_required')}
          isChecked={draft.required}
          onChange={onRequiredChange}
        />
      </div>

      {getOptionsList()}

      <div className="gap-2 flex-row-align-center flex-justify-end _full-width">
        <Button customClasses="spacing-p-t-2" type={ButtonType.SECONDARY} size={ButtonSize.SMALL} text={window.i18next.t('pmwjs_cancel')} onClick={onCancel} />
        <Button customClasses="spacing-p-t-2" type={ButtonType.PRIMARY} size={ButtonSize.SMALL} text={commitLabel} onClick={onCommit} disabled={!canCommit} />
      </div>
    </TicketingQuestionEditorBox>
  );
}
